<template>
  <div class="page" :class="{ mobile: isMobile }">
    <div class="interview-suite" :class="{ mobileSuite: isMobile }">
      <!-- 桌面端顶部栏 -->
      <div v-if="!isMobile" class="top-bar">
        <div class="brand-group">
          <div class="logo" aria-hidden="true">
            <img src="/word_header_logo.png" alt="" />
          </div>
          <div>
            <div class="brand-text">{{ roomTitle }} · 候选人</div>
          </div>
        </div>

        <div class="indicator-group">
          <div class="pill" title="面试计时">
            <span>⏱️</span>
            <span>{{ timerText }}</span>
          </div>
          <div class="pill" title="录制状态（示意）">
            <span :class="['status-dot', recStatusClass]" />
            <span>{{ recStatusText }}</span>
          </div>
          <div class="pill" title="网络状态">
            <span :class="['status-dot', netOnline ? 'ok' : 'bad']" />
            <span>网络：{{ netOnline ? "正常" : "已断开" }}</span>
          </div>
          <el-button size="small" native-type="button" class="primary deskFullscreenBtn" @click="toggleFullscreen">
            {{ isFullscreen ? "退出全屏" : "全屏" }}
          </el-button>
          <!-- 不需要“结束面试”按钮（候选人端由其它流程结束面试） -->
          <!-- <el-button size="small" native-type="button" class="danger" @click="confirmEnd">结束面试</el-button> -->
        </div>
      </div>

      <!-- 主视频卡片 -->
      <div class="video-card" :class="{ mobileVideoCard: isMobile }">
        <div v-if="!isMobile" class="card-header">
          <strong>📹 视频区域</strong>
          <div class="card-header-actions">
            <el-button size="small" native-type="button" @click="toggleMic">
              {{ micOn ? "静音麦克风" : "取消静音" }}
            </el-button>
          </div>
        </div>
        <div class="card-body" :class="{ mobileCardBody: isMobile }">
          <div ref="stage" class="stage" @click="handleStageTap">
            <!-- 移动端顶栏在舞台内，与底部栏共用显隐；全屏后仍能看见 -->
            <div
              v-if="isMobile"
              class="stageMobileTopBar"
              :class="{ hide: !mobileControlsVisible }"
              @click.stop
            >
              <div class="mobileTopSpacer" aria-hidden="true"></div>
              <div class="mobileTimer" aria-label="面试计时">
                <span class="timerText">{{ timerText }}</span>
              </div>
            </div>

            <!-- 面试官主视频流（MAIN：摄像头） -->
            <div v-if="interviewerMainViewId" :id="interviewerMainViewId" class="interviewer-video-box"></div>

            <!-- 面试官共享屏幕流（SUB） -->
            <div
              v-if="interviewerScreenViewId"
              :id="interviewerScreenViewId"
              ref="screenPip"
              class="interviewer-screen-pip"
              :class="{ 'pip-fullscreen-active': screenPipFullscreen, 'pip-rotated': screenPipRotated }"
            >
              <el-button size="small" native-type="button" class="pip-fullscreen-btn" @click.stop="toggleScreenPipFullscreen">
                {{ screenPipFullscreen ? "×" : "⛶" }}
              </el-button>
              <el-button
                v-if="screenPipFullscreen"
                size="small"
                native-type="button"
                class="pip-rotate-btn"
                :class="{ active: screenPipRotated }"
                @click.stop="toggleScreenPipRotate"
              >
                ↻
              </el-button>
            </div>

            <!-- 无主视频时的占位 -->
            <div v-if="!interviewerMainViewId" class="remote-placeholder">
              <div class="ph-message">
                <b>面试官画面</b>
              </div>
            </div>

            <!-- 本地视频 -->
            <div
              class="self-video"
              :style="selfVideoStyle"
              @mousedown="startSelfVideoDrag"
              @touchstart="startSelfVideoDrag"
              @click.stop
            >
              <!-- TRTC 本地视频渲染到该 DOM 容器 -->
              <div id="candidate-self-view" class="candidate-self-view"></div>
            </div>

            <!-- 移动端底部：仅麦克风 -->
            <div
              v-if="isMobile"
              class="mobileBottom"
              :class="{ hide: !mobileControlsVisible }"
              @click.stop
            >
              <div class="mobileControlRow">
                <button
                  type="button"
                  class="mobileAction"
                  :class="{ off: !micOn, disabled: !roomEntered }"
                  :disabled="!roomEntered"
                  @click.stop="toggleMic"
                >
                  <span class="mobileActionIcon" aria-hidden="true">
                    <img :src="micOn ? mobileIcons.micOn : mobileIcons.micOff" alt="" />
                  </span>
                  <span class="mobileActionLabel">{{ micOn ? "麦克风已开" : "麦克风已关" }}</span>
                </button>
              </div>
            </div>

            <!-- 底部状态徽章 -->
            <!-- <div class="hud-badges">
              <span :class="['badge', focusOnPage ? 'ok' : 'warn']">
                焦点：{{ focusOnPage ? "当前页面" : "已离开页面" }}
              </span>
              <span class="badge ok">
                全屏：{{ isFullscreen ? "已开启" : "未要求" }}
              </span>
            </div> -->
          </div>

          <!-- <div class="hint-text">
            ⚡ 提示：请尽量保持在本页面、避免切屏。系统可能会记录切屏/最小化等事件用于复核。
          </div> -->
        </div>
      </div>

      <!-- 本页直接集成 TRTC，不再复用隐藏的 TrtcRoom 组件 -->
    </div>
  </div>
</template>

<script>
import TRTC from "trtc-sdk-v5";
import {
  enterInterviewRoom,
  getInterviewRoomSigByUserId,
  getInterviewRoomStatus,
  getInterviewerEnterTime,
  startInterviewRoomRecording,
} from "@/api/modules/interview/interview";
import { reportAnticheatAction } from "@/api/modules/anticheat/action";
import storage from "@/utils/storage";
import moment from "moment";
import { clearToken } from "@/utils/token";

export default {
  name: "CandidateRoom",
  data() {
    return {
      // TRTC 入房状态（动态从接口拉取 sig）
      trtc: null,
      sdkAppId: 0,
      userId: "",
      userSig: "",
      roomId: null,
      roomEntered: false,

      // 原型布局相关状态
      startedAt: Date.now(),
      timer: 0,
      timerId: null,
      localStream: null,
      micOn: true,
      micToggling: false,
      camOn: true,
      netOnline: navigator.onLine,
      focusOnPage: true,
      isFullscreen: false,
      monitorTimerId: null,
      monitorStorageKey: "candidate_room_anomaly_logs",
      anomalyThrottleMs: 2000,
      minimizeHoldMs: 4000,
      minimizeAbnormalSince: 0,
      lastAnomalyAtMap: {},
      anomalyActiveMap: {},
      anticheatTypeMap: {
        切屏: { action_type: 10 },
        最小化窗口: { action_type: 11 },
        复制粘贴: { action_type: 12 },
        麦克风检查: { action_type: 14 },
        分屏检查: { action_type: 15 },
        浏览器屏幕大小检查: { action_type: 16 },
        页面失焦: { action_type: 17 },
      },

      // 面试官（boss）userId：bossId_${interviewId}
      bossUserId: "",
      // 面试官主流（MAIN：摄像头）
      interviewerMainViewId: "",
      // 面试官屏幕共享（SUB：画中画）
      interviewerScreenViewId: "",
      // 画中画全屏态（与舞台全屏分开区分）
      screenPipFullscreen: false,
      // 共享画面在全屏态下是否旋转（横屏阅读）
      screenPipRotated: false,
      roomStatusPollTimer: null,
      roomEndedHandling: false,
      selfVideoPos: {
        x: 0,
        y: 0,
      },
      selfVideoInited: false,
      selfVideoDragging: false,
      selfVideoDragStartOffset: {
        x: 0,
        y: 0,
      },

      isMobile: false,
      mobileControlsVisible: true,
      mobileIcons: {
        fullscreenEnter: require("@/assets/icons/放大@3x.png"),
        fullscreenExit: require("@/assets/icons/缩小@3x.png"),
        micOn: require("@/assets/icons/麦克风开@3x.png"),
        micOff: require("@/assets/icons/麦克风关@3x.png"),
      },
    };
  },
  computed: {
    name() {
      return this.$route.query.name || "";
    },
    roomTitle() {
      return this.name ? `${this.name}面试间` : "候选人面试间";
    },
    timerText() {
      const m = Math.floor(this.timer / 60)
        .toString()
        .padStart(2, "0");
      const s = (this.timer % 60).toString().padStart(2, "0");
      return `${m}:${s}`;
    },
    recStatusClass() {
      return this.roomEntered ? "ok" : "warn";
    },
    recStatusText() {
      return this.roomEntered ? "录制：进行中" : "录制：未就绪";
    },
    selfVideoStyle() {
      return {
        left: `${this.selfVideoPos.x}px`,
        top: `${this.selfVideoPos.y}px`,
      };
    },
  },
  async mounted() {
    this.updateIsMobile();
    await this.initInterviewTimerStartAt();
    this.startTimer();
    this.joinTrtcRoom();
    this.startRoomStatusPolling();
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
    window.addEventListener("blur", this.handleBlur);
    window.addEventListener("focus", this.handleFocus);
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("copy", this.handleCopy);
    document.addEventListener("paste", this.handlePaste);
    document.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange);
    this.startMonitorTimer();
    this.checkMinimizeWindow();
    this.checkBrowserViewportSize();
    this.checkMultiDisplay();
    this.$nextTick(() => {
      this.initSelfVideoPosition();
    });
  },
  beforeDestroy() {
    this.stopTimer();
    this.exitTrtcRoom();
    this.stopLocalMedia();
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    window.removeEventListener("blur", this.handleBlur);
    window.removeEventListener("focus", this.handleFocus);
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("copy", this.handleCopy);
    document.removeEventListener("paste", this.handlePaste);
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange);
    this.stopMonitorTimer();
    this.stopRoomStatusPolling();
    this.stopSelfVideoDrag();
  },
  methods: {
    updateIsMobile() {
      if (typeof window === "undefined") {
        this.isMobile = false;
        return;
      }
      const width = Number(window.innerWidth) || 0;
      const mediaMatched = typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 768px)").matches : false;
      const ua = String((typeof navigator !== "undefined" && navigator.userAgent) || "").toLowerCase();
      const uaMobile = /android|iphone|ipad|ipod|mobile/.test(ua);
      // 候选人端在部分安卓 WebView 中会出现 viewport 宽度异常，增加 UA + 媒体查询兜底。
      this.isMobile = width <= 768 || mediaMatched || uaMobile;
      if (!this.isMobile) this.mobileControlsVisible = true;
      this.initSelfVideoPosition();
    },
    handleStageTap() {
      if (!this.isMobile) return;
      if (this.selfVideoDragging) return;
      this.mobileControlsVisible = !this.mobileControlsVisible;
    },
    clampSelfVideoPosition(x, y) {
      const stage = this.$refs.stage;
      const selfEl = this.$el && this.$el.querySelector ? this.$el.querySelector(".self-video") : null;
      if (!stage || !selfEl) return { x: 0, y: 0 };
      const maxX = Math.max(0, stage.clientWidth - selfEl.offsetWidth);
      const maxY = Math.max(0, stage.clientHeight - selfEl.offsetHeight);
      return {
        x: Math.min(Math.max(0, Number(x) || 0), maxX),
        y: Math.min(Math.max(0, Number(y) || 0), maxY),
      };
    },
    initSelfVideoPosition(forceReset = false) {
      this.$nextTick(() => {
        const stage = this.$refs.stage;
        const selfEl = this.$el && this.$el.querySelector ? this.$el.querySelector(".self-video") : null;
        if (!stage || !selfEl) return;

        if (!this.selfVideoInited || forceReset) {
          const horizontalGap = this.isMobile ? 14 : 16;
          const verticalGap = this.isMobile ? 62 : 16;
          const defaultX = Math.max(0, stage.clientWidth - selfEl.offsetWidth - horizontalGap);
          const defaultY = this.isMobile
            ? verticalGap
            : Math.max(0, stage.clientHeight - selfEl.offsetHeight - verticalGap);
          const next = this.clampSelfVideoPosition(defaultX, defaultY);
          this.selfVideoPos = { x: next.x, y: next.y };
          this.selfVideoInited = true;
          return;
        }

        const clamped = this.clampSelfVideoPosition(this.selfVideoPos.x, this.selfVideoPos.y);
        this.selfVideoPos = { x: clamped.x, y: clamped.y };
      });
    },
    getSelfVideoPointerPosition(e) {
      if (!e) return null;
      if (e.touches && e.touches.length) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      if (e.changedTouches && e.changedTouches.length) {
        return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
      }
      if (typeof e.clientX === "number" && typeof e.clientY === "number") {
        return { x: e.clientX, y: e.clientY };
      }
      return null;
    },
    startSelfVideoDrag(e) {
      const pointer = this.getSelfVideoPointerPosition(e);
      if (!pointer) return;
      if (e.type === "mousedown" && e.button !== 0) return;

      const selfEl = this.$el && this.$el.querySelector ? this.$el.querySelector(".self-video") : null;
      if (!selfEl) return;
      const rect = selfEl.getBoundingClientRect();
      this.selfVideoDragStartOffset = {
        x: pointer.x - rect.left,
        y: pointer.y - rect.top,
      };
      this.selfVideoDragging = true;

      window.addEventListener("mousemove", this.onSelfVideoDragMove);
      window.addEventListener("mouseup", this.stopSelfVideoDrag);
      window.addEventListener("touchmove", this.onSelfVideoDragMove, { passive: false });
      window.addEventListener("touchend", this.stopSelfVideoDrag);
      window.addEventListener("touchcancel", this.stopSelfVideoDrag);

      if (e.cancelable) e.preventDefault();
    },
    onSelfVideoDragMove(e) {
      if (!this.selfVideoDragging) return;
      const pointer = this.getSelfVideoPointerPosition(e);
      if (!pointer) return;

      const stage = this.$refs.stage;
      if (!stage) return;
      const stageRect = stage.getBoundingClientRect();

      const nextX = pointer.x - stageRect.left - this.selfVideoDragStartOffset.x;
      const nextY = pointer.y - stageRect.top - this.selfVideoDragStartOffset.y;
      const clamped = this.clampSelfVideoPosition(nextX, nextY);
      this.selfVideoPos = { x: clamped.x, y: clamped.y };

      if (e.cancelable) e.preventDefault();
    },
    stopSelfVideoDrag() {
      this.selfVideoDragging = false;
      window.removeEventListener("mousemove", this.onSelfVideoDragMove);
      window.removeEventListener("mouseup", this.stopSelfVideoDrag);
      window.removeEventListener("touchmove", this.onSelfVideoDragMove);
      window.removeEventListener("touchend", this.stopSelfVideoDrag);
      window.removeEventListener("touchcancel", this.stopSelfVideoDrag);
    },
    startRoomStatusPolling() {
      this.stopRoomStatusPolling();
      this.pollRoomStatusOnce();
      this.roomStatusPollTimer = window.setInterval(() => {
        this.pollRoomStatusOnce();
      }, 5000);
    },
    stopRoomStatusPolling() {
      if (!this.roomStatusPollTimer) return;
      window.clearInterval(this.roomStatusPollTimer);
      this.roomStatusPollTimer = null;
    },
    async pollRoomStatusOnce() {
      if (this.roomEndedHandling) return;
      const id = this.getInterviewIdFromRoute() || String(this.roomId || "").trim();
      if (!id) return;
      try {
        const res = await getInterviewRoomStatus(id);
        const data = (res && res.data) ? res.data : {};
        const ok = !!(res && (res.success === true || res.code === 200));
        if (!ok) return;
        if (Number(data.room_status) === 0) {
          await this.handleInterviewEnded();
        }
      } catch (_) {
        // 轮询失败不打断面试流程，等待下一次轮询
      }
    },
    clearCandidateLoginState() {
      try {
        sessionStorage.removeItem("candidate_invite_info");
        sessionStorage.removeItem("candidate_interview_id");
        sessionStorage.removeItem("user_info");
      } catch (_) {
        // ignore
      }
      clearToken();
      if (this.$store && typeof this.$store.commit === "function") {
        this.$store.commit("setAuth", { accessToken: "", tokenType: "", userInfo: null });
      }
    },
    async handleInterviewEnded() {
      if (this.roomEndedHandling) return;
      this.roomEndedHandling = true;
      this.stopRoomStatusPolling();
      this.stopMonitorTimer();
      this.stopTimer();
      await this.exitTrtcRoom();
      this.stopLocalMedia();
      try {
        await this.$alert("面试已结束", "提示", {
          confirmButtonText: "确定",
          type: "warning",
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showClose: false,
        });
      } catch (_) {
        // ignore
      }
      this.clearCandidateLoginState();
      this.$router.push({ path: "/candidate/login" });
    },
    shouldAutoJoinTrtc() {
      // 旧逻辑：已改为组件内 joinTrtcRoom 直接入房
      return false;
    },
    shouldRecord(type) {
      const now = Date.now();
      const last = this.lastAnomalyAtMap[type] || 0;
      if (now - last < this.anomalyThrottleMs) return false;
      this.lastAnomalyAtMap[type] = now;
      return true;
    },
    isAnomalyActive(type) {
      return !!(this.anomalyActiveMap && this.anomalyActiveMap[type]);
    },
    setAnomalyActive(type, active) {
      this.$set(this.anomalyActiveMap, type, !!active);
    },
    /**
     * 状态型异常：只在“正常 -> 异常”时记录/上报一次；
     * 当恢复正常时重置状态，下一次再异常才会再次上报。
     */
    recordAnomalyState(type, isAbnormal, detail = {}) {
      const wasActive = this.isAnomalyActive(type);
      if (isAbnormal) {
        if (!wasActive) {
          this.setAnomalyActive(type, true);
          this.recordAnomaly(type, detail);
        }
        return;
      }
      // recovered
      if (wasActive) this.setAnomalyActive(type, false);
    },
    checkMinimizeWindow() {
      const snapshot = this.getViewportSnapshot();
      const isMinimized = this.isLikelyMinimizedOnDesktop(snapshot);
      const now = Date.now();
      if (isMinimized) {
        if (!this.minimizeAbnormalSince) {
          this.minimizeAbnormalSince = now;
          if (!this.isAnomalyActive("最小化窗口")) {
            this.setAnomalyActive("最小化窗口", true);
            this.recordAnomaly("最小化窗口", {
              ...snapshot,
              trigger: "suspected",
              holdThresholdMs: this.minimizeHoldMs,
            });
          }
        }
        const sustainedMs = now - this.minimizeAbnormalSince;
        const isSustained = sustainedMs >= this.minimizeHoldMs;
        // eslint-disable-next-line no-console
        console.log("[CandidateRoom][MinimizeCheck] suspected:", {
          sustainedMs,
          holdThresholdMs: this.minimizeHoldMs,
          snapshot,
        });
        if (isSustained && !this.isAnomalyActive("最小化窗口")) {
          // eslint-disable-next-line no-console
          console.log("[CandidateRoom][MinimizeCheck] sustained -> will report anomaly");
        }
        return;
      }
      if (this.minimizeAbnormalSince) {
        // eslint-disable-next-line no-console
        console.log("[CandidateRoom][MinimizeCheck] recovered before sustained threshold");
      }
      this.minimizeAbnormalSince = 0;
      if (this.isAnomalyActive("最小化窗口")) this.setAnomalyActive("最小化窗口", false);
    },
    getViewportSnapshot() {
      return {
        innerWidth: window.innerWidth || 0,
        innerHeight: window.innerHeight || 0,
        outerWidth: window.outerWidth || 0,
        outerHeight: window.outerHeight || 0,
        screenX: typeof window.screenX === "number" ? window.screenX : 0,
        screenY: typeof window.screenY === "number" ? window.screenY : 0,
        visibilityState: document.visibilityState,
        hasFocus: typeof document.hasFocus === "function" ? document.hasFocus() : true,
      };
    },
    isDesktopUA() {
      const ua = String((typeof navigator !== "undefined" && navigator.userAgent) || "").toLowerCase();
      // 仅 PC 端启用最小化识别，移动端后台/切应用不按“最小化窗口”处理
      return !/android|iphone|ipad|ipod|mobile/.test(ua);
    },
    isLikelyMinimizedOnDesktop(snapshot = {}) {
      if (!this.isDesktopUA()) return false;
      const zeroSize =
        Number(snapshot.innerWidth) === 0 ||
        Number(snapshot.innerHeight) === 0 ||
        Number(snapshot.outerWidth) === 0 ||
        Number(snapshot.outerHeight) === 0;
      // Windows 上最小化窗口常见特征：窗口坐标回落到 -32000 附近
      const offscreenMinimized = Number(snapshot.screenX) <= -30000 || Number(snapshot.screenY) <= -30000;
      // 某些浏览器最小化后会出现极小窗口尺寸
      const tinyWindow = Number(snapshot.outerWidth) <= 160 && Number(snapshot.outerHeight) <= 80;
      const hiddenAndBlurred = snapshot.visibilityState === "hidden" && snapshot.hasFocus === false;
      return zeroSize || offscreenMinimized || (hiddenAndBlurred && tinyWindow);
    },
    getInterviewIdFromRoute() {
      try {
        return String(sessionStorage.getItem("candidate_interview_id") || "").trim();
      } catch (_) {
        return "";
      }
    },
    getAnticheatClientInfo() {
      const ua = (typeof navigator !== "undefined" && navigator.userAgent) ? navigator.userAgent : "";
      const sw = (typeof screen !== "undefined" && screen.width) ? screen.width : 0;
      const sh = (typeof screen !== "undefined" && screen.height) ? screen.height : 0;
      return `${ua} | ${sw}x${sh}`;
    },
    async reportAnticheat(type, detail = {}) {
      const map = this.anticheatTypeMap && this.anticheatTypeMap[type] ? this.anticheatTypeMap[type] : null;
      if (!map) return;
      const interviewId = this.getInterviewIdFromRoute();
      if (!interviewId) return;
      try {
        await reportAnticheatAction({
          interview_id: interviewId,
          action_type: map.action_type,
          action_time: moment().format("YYYY-MM-DD HH:mm:ss"),
          client_info: this.getAnticheatClientInfo(),
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[CandidateRoom][Anticheat] report failed:", e);
      }
    },
    recordAnomaly(type, detail = {}) {
      if (!this.shouldRecord(type)) return;
      const payload = {
        type,
        detail,
        ts: Date.now(),
        time: new Date().toISOString(),
      };
      const history = storage.get(this.monitorStorageKey, []);
      const logs = Array.isArray(history) ? history : [];
      logs.push(payload);
      if (logs.length > 500) logs.splice(0, logs.length - 500);
      storage.set(this.monitorStorageKey, logs);
      console.log("[CandidateRoom][Anomaly]", payload);

      // 异常行为上报
      this.reportAnticheat(type, detail);
    },
    startMonitorTimer() {
      this.stopMonitorTimer();
      this.monitorTimerId = window.setInterval(() => {
        this.checkMicrophoneStatus();
        this.checkMinimizeWindow();
        this.checkBrowserViewportSize();
        this.checkMultiDisplay();
      }, 3000);
    },
    stopMonitorTimer() {
      if (!this.monitorTimerId) return;
      window.clearInterval(this.monitorTimerId);
      this.monitorTimerId = null;
    },
    checkMicrophoneStatus() {
      // 本页音频采集交由 TRTC，无法稳定拿到原始 MediaStream，因此用状态兜底判断。
      if (!this.roomEntered) return;
      this.recordAnomalyState("麦克风检查", !this.micOn, { reason: "检测到麦克风关闭或异常" });
    },
    checkBrowserViewportSize() {
      const sw = (window.screen && window.screen.width) || 0;
      const sh = (window.screen && window.screen.height) || 0;
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;
      if (!sw || !sh || !vw || !vh) return;
      const ratio = (vw * vh) / (sw * sh);
      const isAbnormal = ratio < 0.35;
      this.recordAnomalyState("浏览器屏幕大小检查", isAbnormal, {
        ratio: Number(ratio.toFixed(3)),
        viewport: `${vw}x${vh}`,
        screen: `${sw}x${sh}`,
      });
    },
    checkMultiDisplay() {
      const hasExtendedScreen = typeof window.screen !== "undefined" && typeof window.screen.isExtended === "boolean"
        ? window.screen.isExtended
        : false;
      const likelySecondScreen = typeof window.screenX === "number" && typeof window.screen.width === "number"
        ? window.screenX < -20 || window.screenX > window.screen.width + 20
        : false;
      const isAbnormal = hasExtendedScreen || likelySecondScreen;
      this.recordAnomalyState("分屏检查", isAbnormal, {
        isExtended: hasExtendedScreen,
        screenX: window.screenX,
        screenWidth: window.screen && window.screen.width,
      });
    },
    async joinTrtcRoom() {
      try {
        const rawInterviewId = this.getInterviewIdFromRoute();
        if (!rawInterviewId) {
          this.$message.error("缺少 interview_id，无法加入 TRTC 房间");
          return;
        }

        const trtcRoomIdNum = Number.parseInt(rawInterviewId, 10);
        const roomId = Number.isFinite(trtcRoomIdNum) ? trtcRoomIdNum : rawInterviewId;
        this.roomId = roomId;

        // 自己入房 userId：userId_${interviewId}
        const requestUserId = `userId_${rawInterviewId}`;

        // 面试官 bossId：bossId_${interviewId}
        this.bossUserId = `bossId_${rawInterviewId}`;
        this.interviewerMainViewId = "";
        this.interviewerScreenViewId = "";
        // debug: 确认 interviewId 与 TRTC userId 映射
        // eslint-disable-next-line no-console
        console.log("[CandidateRoom][TRTC] interviewId:", rawInterviewId, "requestUserId:", requestUserId, "bossUserId:", this.bossUserId);

        const sigRes = await getInterviewRoomSigByUserId(requestUserId);
        const sigData = sigRes && sigRes.data ? sigRes.data : sigRes;

        const sdkAppId = sigData && sigData.sdkAppId;
        const userId = sigData && sigData.userId;
        const userSig = sigData && sigData.userSig;

        if (!sdkAppId || !userId || !userSig) {
          this.$message.error("sig 接口返回缺少入房参数");
          return;
        }

        this.trtc = TRTC.create();
        this.installEventHandlers();
        await this.trtc.enterRoom({
          roomId,
          sdkAppId: Number(sdkAppId),
          userId: String(userId),
          userSig: String(userSig),
        });

        await this.trtc.startLocalAudio();
        await this.trtc.startLocalVideo({ view: "candidate-self-view" });

        this.micOn = true;
        this.camOn = true;
        this.roomEntered = true;

        // 通知后端：候选人已进入面试间（固定 room_status = 2）
        try {
          await enterInterviewRoom(roomId, 2);
          await startInterviewRoomRecording(rawInterviewId);
        } catch (e) {
          // 不阻断进入房间体验
          // eslint-disable-next-line no-console
          console.error("[CandidateRoom][enter] start failed:", e);
        }
      } catch (e) {
        this.roomEntered = false;
        this.$message.error(e?.message || "进入 TRTC 房间失败");
      }
    },
    async exitTrtcRoom() {
      if (!this.trtc) return;
      try {
        await this.trtc.exitRoom();
      } catch (_) {
        // noop
      } finally {
        try {
          this.uninstallEventHandlers();
        } catch (_) {}
        this.trtc = null;
        this.roomEntered = false;
        this.interviewerMainViewId = "";
        this.interviewerScreenViewId = "";
      }
    },
    async startLocalMedia() {
      // deprecated: 本页 TRTC 采集由 joinTrtcRoom 处理，避免重复触发摄像头/麦克风权限请求。
      this.localStream = null;
    },
    stopLocalMedia() {
      if (!this.localStream) return;
      try {
        this.localStream.getTracks().forEach((t) => t.stop());
      } catch (_) {
        // noop
      }
      this.localStream = null;
    },
    startTimer() {
      this.stopTimer();
      this.updateTimer();
      this.timerId = window.setInterval(() => {
        this.updateTimer();
      }, 300);
    },
    updateTimer() {
      this.timer = Math.max(0, Math.floor((Date.now() - this.startedAt) / 1000));
    },
    parseServerTimeToMs(raw) {
      if (raw === null || raw === undefined || raw === "") return null;
      if (raw instanceof Date) {
        const ms = raw.getTime();
        return Number.isFinite(ms) ? ms : null;
      }
      if (typeof raw === "number" && Number.isFinite(raw)) {
        if (raw > 1e12) return raw;
        if (raw > 1e9) return raw * 1000;
        return null;
      }
      if (typeof raw === "string") {
        const s = raw.trim();
        if (!s) return null;
        if (/^\d+(\.\d+)?$/.test(s)) {
          return this.parseServerTimeToMs(Number(s));
        }
        const direct = Date.parse(s);
        if (Number.isFinite(direct)) return direct;
        const normalized = Date.parse(s.replace(/-/g, "/"));
        return Number.isFinite(normalized) ? normalized : null;
      }
      return null;
    },
    async initInterviewTimerStartAt() {
      this.startedAt = Date.now();
      const interviewId = this.getInterviewIdFromRoute() || String(this.roomId || "").trim();
      if (!interviewId) return;
      try {
        const res = await getInterviewerEnterTime(interviewId);
        const data = (res && res.data) || {};
        const enterAtMs = this.parseServerTimeToMs(data.interviewer_enter_time);
        if (!Number.isFinite(enterAtMs) || enterAtMs <= 0) return;
        this.startedAt = Math.min(enterAtMs, Date.now());
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[CandidateRoom][getInterviewerEnterTime] failed:", e);
      }
    },
    stopTimer() {
      if (this.timerId) {
        window.clearInterval(this.timerId);
        this.timerId = null;
      }
    },
    async toggleMic() {
      if (!this.trtc || !this.roomEntered) return;
      if (this.micToggling) return;
      this.micToggling = true;
      try {
        // 这里不再依赖 muteLocalAudio：部分环境下它可能“调用成功但实际上未停止上行音频”
        if (this.micOn) {
          await this.trtc.stopLocalAudio();
          this.micOn = false;
          this.recordAnomaly("麦克风检查", { reason: "用户手动关闭麦克风" });
        } else {
          await this.trtc.startLocalAudio();
          this.micOn = true;
        }
      } catch (e) {
        this.$message && this.$message.warning && this.$message.warning(e?.message || "麦克风切换失败");
      } finally {
        this.micToggling = false;
      }
    },
    getFullscreenElement() {
      const d = document;
      return d.fullscreenElement || d.webkitFullscreenElement || d.mozFullScreenElement || d.msFullscreenElement || null;
    },
    async toggleFullscreen() {
      const el = this.$refs.stage;
      if (!el) return;
      try {
        if (!this.getFullscreenElement()) {
          const req =
            el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
          if (req) await req.call(el);
        } else {
          const exit =
            document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
          if (exit) await exit.call(document);
        }
      } catch (_) {
        this.$message.warning("全屏失败：浏览器可能阻止了全屏请求");
      }
      await this.$nextTick();
      this.handleFullscreenChange();
      if (this.isMobile && this.isFullscreen) this.mobileControlsVisible = true;
    },
    installEventHandlers() {
      if (!this.trtc) return;
      this.trtc.on(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, this.handleRemoteVideoAvailable);
      this.trtc.on(TRTC.EVENT.REMOTE_VIDEO_UNAVAILABLE, this.handleRemoteVideoUnavailable);
    },
    uninstallEventHandlers() {
      if (!this.trtc) return;
      this.trtc.off(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, this.handleRemoteVideoAvailable);
      this.trtc.off(TRTC.EVENT.REMOTE_VIDEO_UNAVAILABLE, this.handleRemoteVideoUnavailable);
    },
    handleRemoteVideoAvailable(event) {
      const { userId, streamType } = event || {};
      if (!userId || streamType === undefined || streamType === null) return;
      // eslint-disable-next-line no-console
      console.log("[CandidateRoom][TRTC] REMOTE_VIDEO_AVAILABLE event:", { userId, streamType, bossUserId: this.bossUserId });
      // 只渲染面试官摄像头（video）画面
      if (!this.bossUserId || userId !== this.bossUserId) return;

      const viewId = `${userId}_${streamType}`;
      const isMain = String(streamType) === String(TRTC.TYPE.STREAM_TYPE_MAIN);
      const isSub = String(streamType) === String(TRTC.TYPE.STREAM_TYPE_SUB);

      if (isMain) this.interviewerMainViewId = viewId;
      else if (isSub) this.interviewerScreenViewId = viewId;
      else this.interviewerMainViewId = viewId;

      this.$nextTick(async () => {
        try {
          await this.trtc.startRemoteVideo({ userId, streamType, view: viewId });
        } catch (e) {
          this.$message.warning(`拉取面试官远端视频失败：${e?.message || "unknown"}`);
        }
      });
    },
    handleRemoteVideoUnavailable(event) {
      const { userId, streamType } = event || {};
      if (!userId || streamType === undefined || streamType === null) return;
      // eslint-disable-next-line no-console
      console.log("[CandidateRoom][TRTC] REMOTE_VIDEO_UNAVAILABLE event:", { userId, streamType, bossUserId: this.bossUserId });
      if (!this.bossUserId || userId !== this.bossUserId) return;

      const viewId = `${userId}_${streamType}`;
      const isMain = String(streamType) === String(TRTC.TYPE.STREAM_TYPE_MAIN);
      const isSub = String(streamType) === String(TRTC.TYPE.STREAM_TYPE_SUB);

      try {
        this.trtc.stopRemoteVideo({ userId, streamType });
      } catch (_) {}
      if (isMain && this.interviewerMainViewId === viewId) this.interviewerMainViewId = "";
      if (isSub && this.interviewerScreenViewId === viewId) this.interviewerScreenViewId = "";
    },
    handleOnline() {
      this.netOnline = true;
    },
    handleOffline() {
      this.netOnline = false;
    },
    handleBlur() {
      this.focusOnPage = false;
      this.recordAnomaly("页面失焦", { visibilityState: document.visibilityState });
      this.checkMinimizeWindow();
    },
    handleFocus() {
      this.focusOnPage = true;
      this.checkMinimizeWindow();
    },
    handleResize() {
      this.updateIsMobile();
      this.checkMinimizeWindow();
      this.checkBrowserViewportSize();
      this.checkMultiDisplay();
    },
    handleCopy() {
      this.recordAnomaly("复制粘贴", { action: "copy" });
    },
    handlePaste() {
      this.recordAnomaly("复制粘贴", { action: "paste" });
    },
    handleKeydown(e) {
      const key = (e && e.key ? String(e.key) : "").toLowerCase();
      const withCtrlOrMeta = !!(e && (e.ctrlKey || e.metaKey));
      const isCopyPasteCut = withCtrlOrMeta && (key === "c" || key === "v" || key === "x");
      const isShiftInsert = !!(e && e.shiftKey && key === "insert");
      if (isCopyPasteCut || isShiftInsert) {
        this.recordAnomaly("复制粘贴", {
          action: "keyboard",
          key: e && e.key,
          ctrlKey: !!(e && e.ctrlKey),
          metaKey: !!(e && e.metaKey),
          shiftKey: !!(e && e.shiftKey),
        });
      }
    },
    handleVisibilityChange() {
      this.focusOnPage = document.visibilityState === "visible";
      if (document.visibilityState === "hidden") {
        this.recordAnomaly("切屏", { visibilityState: document.visibilityState });
      }
      this.checkMinimizeWindow();
    },
    handleFullscreenChange() {
      const fsEl = this.getFullscreenElement();
      const stage = this.$refs.stage;
      const pipEl = this.$refs.screenPip;
      const isStageFs = !!(
        stage &&
        fsEl &&
        (fsEl === stage || (typeof stage.contains === "function" && stage.contains(fsEl)))
      );
      this.isFullscreen = isStageFs;
      this.screenPipFullscreen = !!(
        pipEl &&
        fsEl &&
        (fsEl === pipEl || (typeof pipEl.contains === "function" && pipEl.contains(fsEl)))
      );
      if (!this.screenPipFullscreen) this.screenPipRotated = false;
      this.initSelfVideoPosition();
    },
    async toggleScreenPipFullscreen() {
      const pipEl = this.$refs.screenPip;
      if (!pipEl) return;
      const fs = this.getFullscreenElement();
      const req = pipEl.requestFullscreen || pipEl.webkitRequestFullscreen || pipEl.mozRequestFullScreen || pipEl.msRequestFullscreen;
      const exit =
        document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
      try {
        if (fs) {
          if (fs === pipEl || (typeof pipEl.contains === "function" && pipEl.contains(fs))) {
            if (exit) await exit.call(document);
          } else {
            if (exit) await exit.call(document);
            if (req) await req.call(pipEl);
          }
        } else if (req) {
          await req.call(pipEl);
        }
      } catch (e) {
        this.$message.warning("全屏失败：浏览器阻止了全屏请求");
      }
      await this.$nextTick();
      this.handleFullscreenChange();
    },
    toggleScreenPipRotate() {
      if (!this.screenPipFullscreen) return;
      this.screenPipRotated = !this.screenPipRotated;
    },
    async confirmEnd() {
      try {
        await this.$confirm("结束后将停止本地设备采集并返回入场页，确定结束面试吗？", "结束面试确认", {
          type: "warning",
        });
      } catch (_) {
        return;
      }

      await this.exitTrtcRoom();
      this.stopLocalMedia();

      // 退出音视频房间后，通知后端更新房间状态：固定 room_status = 0
      try {
        const id = this.roomId != null ? this.roomId : this.getInterviewIdFromRoute();
        if (id) await enterInterviewRoom(id, 0);
      } catch (e) {
        // 这里不阻断返回打分页/回入场流程
        // eslint-disable-next-line no-console
        console.error("[CandidateRoom][enter] end failed:", e);
      }

      this.backToEntry();
    },
    backToEntry() {
      this.$router.push({ path: "/candidate/entry", query: { ...this.$route.query } });
    },
  },
};
</script>

<style scoped>
.page {
  min-height: 100%;
  padding: 18px 16px 26px;
  background: #f5f7fa;
}

.interview-suite {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 900px;
}

.top-bar {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.brand-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo {
  width: 90px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
}

.logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.brand-text {
  font-weight: 900;
  font-size: 16px;
}

.brand-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.indicator-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
}

.status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}
.status-dot.ok {
  background: #22c55e;
}
.status-dot.warn {
  background: #f59e0b;
}
.status-dot.bad {
  background: #ef4444;
}

button {
  border-radius: 11px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: #ffffff;
  color: #1a1a2e;
  transition: all 0.1s;
  font-family: inherit;
}
button:hover {
  background: #f8fafc;
}
button.primary {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  border-color: #2563eb;
  color: #fff;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
}
button.primary:hover {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}
button.danger {
  background: #fee2e2;
  border-color: rgba(239, 68, 68, 0.3);
  color: #b91c1c;
}
button.danger:hover {
  background: #fecaca;
}
button:active {
  transform: scale(0.98);
}

.video-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-header {
  padding: 14px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
.card-header strong {
  font-weight: 900;
  font-size: 15px;
}
.card-header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.card-body {
  padding: 18px;
}

.stage {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f1f5f9;
}

.interviewer-video-box {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: #000;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.interviewer-video-box video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
}

/* 兼容 TRTC 注入的内部容器，强制面试官主画面居中展示 */
.interviewer-video-box /deep/ video,
.interviewer-video-box /deep/ canvas {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  object-position: center center !important;
}

.interviewer-screen-pip {
  position: absolute;
  right: 14px;
  top: 14px;
  width: min(34%, 240px);
  aspect-ratio: 16/9;
  z-index: 2;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid rgba(0, 0, 0, 0.12);
  background: #0b1120;
}

.interviewer-screen-pip video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.pip-fullscreen-active {
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100% !important;
  height: 100% !important;
  border-radius: 0 !important;
  z-index: 9999;
}

.pip-fullscreen-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(0, 0, 0, 0.35);
  color: #ffffff;
  cursor: pointer;
  font-weight: 900;
  line-height: 32px;
  text-align: center;
  padding: 0;
}

.pip-fullscreen-btn:hover {
  background: rgba(0, 0, 0, 0.48);
}

.pip-rotate-btn {
  position: absolute;
  top: 10px;
  right: 52px;
  z-index: 3;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(0, 0, 0, 0.35);
  color: #ffffff;
  cursor: pointer;
  font-weight: 900;
  line-height: 32px;
  text-align: center;
  padding: 0;
}

.pip-rotate-btn:hover {
  background: rgba(0, 0, 0, 0.48);
}

.pip-rotate-btn.active {
  background: rgba(30, 100, 180, 0.45);
  border-color: rgba(159, 214, 255, 0.8);
}

.interviewer-screen-pip.pip-rotated /deep/ video,
.interviewer-screen-pip.pip-rotated /deep/ canvas {
  position: absolute !important;
  top: 50%;
  left: 50%;
  width: 100vh !important;
  height: 100vw !important;
  max-width: none !important;
  max-height: none !important;
  object-fit: contain !important;
  transform: translate(-50%, -50%) rotate(90deg);
  transform-origin: center center;
}

.remote-placeholder {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #1a1a2e;
  z-index: 1;
}

.ph-message {
  text-align: center;
  max-width: 560px;
  padding: 16px 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.03);
}
.ph-message b {
  font-weight: 900;
}
.ph-message p {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
}

.self-video {
  position: absolute;
  left: 16px;
  top: 16px;
  width: min(32%, 240px);
  aspect-ratio: 16 / 9;
  border-radius: 14px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
  background: #1e293b;
  z-index: 3;
  cursor: move;
  touch-action: none;
}

.candidate-self-view {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  background: #1e293b;
}

/* 兼容 TRTC 注入的本地预览节点，避免本地画面出现下沉偏移 */
.candidate-self-view /deep/ video,
.candidate-self-view /deep/ canvas {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  object-position: center center !important;
}

.hud-badges {
  position: absolute;
  left: 14px;
  bottom: 14px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  pointer-events: none;
}

.badge {
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(0, 0, 0, 0.05);
  color: #1a1a2e;
  font-weight: 500;
}
.badge.ok {
  background: rgba(34, 197, 94, 0.15);
  color: #ffffff;
  border-color: rgba(34, 197, 94, 0.3);
}
.badge.warn {
  background: rgba(245, 158, 11, 0.12);
  color: #ffffff;
  border-color: rgba(245, 158, 11, 0.3);
}

.hint-text {
  margin-top: 12px;
  font-size: 12px;
  color: #6b7280;
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.trtc-hidden {
  display: none;
}

/* 移动端全屏布局（与 InterviewerRoom 一致，由 isMobile 切换） */
.page.mobile {
  min-width: 0;
  padding: 0;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  background: #000;
}

.interview-suite.mobileSuite {
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 0;
  height: 100vh;
  gap: 0;
}

.video-card.mobileVideoCard {
  border-radius: 0;
  border: none;
  box-shadow: none;
  background: transparent;
  overflow: visible;
}

.card-body.mobileCardBody {
  padding: 0;
}

.page.mobile .stage {
  border-radius: 0;
  height: 100vh;
  max-height: 100vh;
  aspect-ratio: auto;
  border: none;
  background: #0b1120;
}

.page.mobile .remote-placeholder {
  color: rgba(255, 255, 255, 0.86);
  z-index: 0;
}

.page.mobile .ph-message {
  max-width: calc(100% - 24px);
  margin: 0 12px;
  padding: 16px 18px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px dashed rgba(255, 255, 255, 0.2);
  box-shadow: none;
  color: rgba(255, 255, 255, 0.92);
}

.page.mobile .ph-message b {
  color: rgba(255, 255, 255, 0.96);
}

.page.mobile .ph-message p {
  color: #d1d5db;
}

.page.mobile .self-video {
  left: auto;
  right: 14px;
  top: calc(14px + env(safe-area-inset-top, 0px) + 34px);
  width: min(31vw, 120px);
  min-width: 104px;
  aspect-ratio: 3 / 5;
  border-radius: 14px;
  border-width: 2px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.4);
}

.page.mobile .interviewer-screen-pip {
  right: 10px;
  top: calc(14px + env(safe-area-inset-top, 0px) + 110px);
  width: min(44%, 200px);
  border-radius: 10px;
}

/* 移动端顶栏在舞台内绝对定位，全屏后仍在画面层内，与底栏共用显隐 */
.page.mobile .stageMobileTopBar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 55;
  display: grid;
  grid-template-columns: 42px 1fr 42px;
  align-items: center;
  gap: 0;
  padding: calc(10px + env(safe-area-inset-top, 0px)) 12px 10px;
  background: transparent;
  border: none;
  box-shadow: none;
  transition: transform 0.18s ease, opacity 0.18s ease;
  pointer-events: auto;
}

.page.mobile .stageMobileTopBar.hide {
  transform: translateY(-110%);
  opacity: 0;
  pointer-events: none;
}

/* 与右侧全屏按钮同宽占位，保持计时居中；不展示 logo */
.mobileTopSpacer {
  width: 42px;
  min-width: 42px;
  height: 28px;
  pointer-events: none;
}

.mobileTimer {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 78px;
  height: 28px;
  padding: 0 12px;
  margin: 0 auto;
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 850;
  letter-spacing: 0.4px;
  user-select: none;
}

.mobileTimer .timerText {
  font-variant-numeric: tabular-nums;
}

.mobileTopAction {
  justify-self: end;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
}

.mobileTopAction:hover {
  background: transparent;
}

.mobileTopAction img {
  width: 22px;
  height: 22px;
  display: block;
  object-fit: contain;
}

.mobileTopAction.active {
  opacity: 0.85;
}

/* 底栏：仅麦克风（与 InterviewerRoom 移动端底栏视觉一致） */
.mobileBottom {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 56;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 23px;
  padding: 36px 18px calc(29px + env(safe-area-inset-bottom, 0px));
}

.mobileBottom.hide {
  transform: translateY(110%);
  opacity: 0;
  pointer-events: none;
}

.mobileControlRow {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 44px;
}

.mobileControlRowBottom {
  justify-content: center;
  gap: 0;
}

.mobileAction {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 99px;
  padding: 0;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.96);
  box-shadow: none;
  cursor: pointer;
  font-family: inherit;
}

.mobileAction:hover {
  background: transparent;
}

.mobileAction.disabled {
  opacity: 0.45;
}

.mobileAction.active .mobileActionLabel {
  color: #9fd6ff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85), 0 0 8px rgba(30, 100, 180, 0.45);
}

.mobileAction.off .mobileActionLabel {
  color: rgba(255, 255, 255, 0.82);
}

.mobileAction.off .mobileActionIcon::after {
  content: "";
  position: absolute;
  width: 52px;
  height: 4px;
  border-radius: 999px;
  background: #ff5c5c;
  transform: rotate(-45deg);
}

.mobileActionDanger .mobileActionLabel {
  color: #ffb3b3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8), 0 0 6px rgba(120, 30, 30, 0.35);
}

.mobileActionIcon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 49px;
  height: 49px;
  line-height: 1;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.38);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.mobileActionIcon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.mobileActionLabel {
  position: relative;
  z-index: 1;
  display: block;
  max-width: 109px;
  font-size: 17px;
  line-height: 1.35;
  text-align: center;
  color: rgba(255, 255, 255, 0.94);
  font-weight: 500;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.75), 0 0 1px rgba(0, 0, 0, 0.6);
}

.deskFullscreenBtnImg {
  width: 18px;
  height: 18px;
  display: block;
  object-fit: contain;
}

.deskFullscreenBtn /deep/ span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .page {
    min-width: 0;
    padding: 0;
  }

  .interview-suite {
    min-width: 0;
    max-width: 100%;
    padding: 0;
    height: 100vh;
    gap: 0;
  }
}
</style>

